/**
 * Wave J — paper space / sheet / viewport management for CAD content.
 * The BIM sheet system (kernel sheets, PlacedView, SheetView render) is the
 * single paper-space model — CAD commands bridge into it instead of building
 * a parallel one. (docs/architecture/command-parity-plan.md)
 */

import { runInteraction, PT, PN, PTxt, requireSelection, entityToPolyline } from './lib';
import { useBIMStore } from '../../../state/useBIMStore';
import { useDocumentStore } from '../../../state/useDocumentStore';
import { useAppStore } from '../../../state/store';
import { useViewStore } from '../../../state/commandAuxStores';
import { notify } from '../../../utils/notify';
import { downloadTextFile } from '../../../services/fileSystem/fileAccess';
import { openDxfExportDialog } from '../../../services/cad/dxf/exportDialogBridge';
import type { Pt3 } from '../geometryMath';
import type { RunnerMap } from '../runnerRegistry';
import i18n from '../../../i18n';

/**
 * SVG export of the whole 2D ledger (paper space content). Every drawable
 * CAD entity is tessellated through the shared polyline adapter (line,
 * polyline, polygon, rectangle, circle, arc, ellipse, curve, solid
 * boundary), text becomes an SVG label, and the viewBox is fitted to the
 * real drawing bounds with padding — content is never clipped by a fixed
 * frame like the old `-60 -60 120 120`.
 */
function ledgerSVG(): string {
    const entities = useAppStore.getState().entities;
    const polylines: Pt3[][] = [];
    const texts: { x: number; y: number; content: string }[] = [];
    Object.values(entities).forEach((e) => {
        if (!e.visible) return;
        const d = e.data as Record<string, any>;
        if (e.type === 'text') {
            const p = d.position as number[] | undefined;
            if (p) texts.push({ x: p[0], y: -(p[1] ?? 0), content: String(d.content ?? '') });
            return;
        }
        const pts = entityToPolyline(e);
        if (pts && pts.length >= 2) {
            polylines.push(pts.map((p) => [p[0], -(p[1] ?? 0), p[2] ?? 0] as Pt3));
        }
    });

    // Fit the page to the geometry + labels, with 8% breathing room.
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const absorb = (x: number, y: number) => {
        if (x < minX) minX = x; if (y < minY) minY = y;
        if (x > maxX) maxX = x; if (y > maxY) maxY = y;
    };
    polylines.forEach((poly) => poly.forEach((p) => absorb(p[0], p[1])));
    texts.forEach((t) => absorb(t.x, t.y));
    if (!Number.isFinite(minX)) { minX = -60; minY = -60; maxX = 60; maxY = 60; }
    const pad = Math.max((maxX - minX) * 0.08, (maxY - minY) * 0.08, 1);
    const vbX = minX - pad, vbY = minY - pad;
    const vbW = Math.max(maxX - minX + pad * 2, 1e-6);
    const vbH = Math.max(maxY - minY + pad * 2, 1e-6);
    const stroke = (Math.max(vbW, vbH) * 0.0015).toFixed(4);
    const fontSize = (Math.max(vbW, vbH) * 0.02).toFixed(3);

    const parts: string[] = [];
    polylines.forEach((poly) => {
        const pts = poly.map((p) => `${p[0]},${p[1]}`).join(' ');
        parts.push(`<polyline points="${pts}" fill="none" stroke="black" stroke-width="${stroke}"/>`);
    });
    texts.forEach((t) => {
        const safe = t.content.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        parts.push(`<text x="${t.x}" y="${t.y}" font-size="${fontSize}" font-family="sans-serif">${safe}</text>`);
    });
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vbX} ${vbY} ${vbW} ${vbH}" width="420mm" height="${(420 * vbH / vbW).toFixed(1)}mm"><g>${parts.join('')}</g></svg>`;
}

function sheetIdOrNew(label: string): { id: string; created: boolean } | null {
    const store = useBIMStore.getState();
    if (store.project.activeSheetId && store.project.sheets.has(store.project.activeSheetId)) {
        return { id: store.project.activeSheetId, created: false };
    }
    const sheets = [...store.project.sheets.values()];
    if (sheets.length > 0) return { id: sheets[0].id, created: false };
    notify(i18n.t('tools.notify.noSheet', { label: label }), 'warning');
    return null;
}

export const sheetRunners: RunnerMap = {
    'file.layout': (args) => {
        const store = useBIMStore.getState();
        const sub = (args?.[0] ?? 'list').toLowerCase();
        if (sub === 'new') {
            const n = store.project.sheets.size + 1;
            store.createSheet(String(n), `Layout ${n}`, 'ANSI-A');
            return `LAYOUT: new sheet "Layout ${n}" created (BIM sheet model is the paper space).`;
        }
        const list = [...store.project.sheets.values()].map((s) => `${s.number}:${s.name}`).join(', ');
        return list ? `Sheets: ${list}` : 'No sheets yet — LAYOUT NEW.';
    },

    'file.mview': () => {
        runInteraction({
            steps: [PT('MVIEW: first viewport corner'), PT('MVIEW: opposite corner')],
            complete: (pts) => {
                const sheet = sheetIdOrNew('MVIEW');
                if (!sheet) return 'MVIEW: no sheet available.';
                const store = useBIMStore.getState();
                const viewId = store.project.activeViewId;
                const cx = (pts[0].x + pts[1].x) / 2, cy = (pts[0].y + pts[1].y) / 2;
                store.placeViewOnSheet(sheet.id, viewId, { x: cx / 100, y: cy / 100 });
                store.setActiveSheet(sheet.id);
                return `MVIEW: floating viewport for the active view placed on the sheet.`;
            },
        });
        return 'MVIEW: specify corners';
    },

    'file.pageSetup': (args) => {
        const sheet = sheetIdOrNew('PAGESETUP');
        if (!sheet) return 'PAGESETUP: no sheet.';
        const store = useBIMStore.getState();
        const paper = (args?.[0] ?? 'ANSI-A').toUpperCase();
        store.updateSheet(sheet.id, { style: paper.toLowerCase() });
        return `PAGESETUP: sheet configured (${paper}). Printer drivers are OS-level in Yap.`;
    },

    'file.sheetSet': () => {
        const store = useBIMStore.getState();
        const list = [...store.project.sheets.values()].map((s) => `${s.number} ${s.name}`).join(' | ');
        return list ? `Sheet Set: ${list}` : 'Sheet Set: no sheets.';
    },
    'file.archive': () => {
        const doc = useDocumentStore.getState();
        const payload = {
            format: 'yap-archive', version: 1, savedAt: new Date().toISOString(),
            displayName: doc.displayName,
            ledger: Object.values(doc.sceneObjects),
            layers: useAppStore.getState().layers,
        };
        downloadTextFile(`${doc.displayName || 'project'}-archive.json`, JSON.stringify(payload));
        return 'ARCHIVE: project packaged (geometry + layers) and downloaded.';
    },
    'file.transmit': (args) => (sheetRunners['file.archive'] as (a?: string[]) => string)(args),

    'file.vpClip': () => {
        const sheet = sheetIdOrNew('VPCLIP');
        if (!sheet) return 'VPCLIP: no sheet.';
        const store = useBIMStore.getState();
        const target = store.project.sheets.get(sheet.id);
        if (!target || target.viewsPlaced.length === 0) return 'VPCLIP: place a viewport first (MVIEW).';
        runInteraction({
            steps: [PTxt('VPCLIP: viewport index (0-based)'), PT('VPCLIP: boundary point 1'), PT('VPCLIP: boundary point 2')],
            complete: (pts, values) => {
                const idx = Math.max(0, Math.min(target.viewsPlaced.length - 1, Math.round(Number(values[0]) || 0)));
                const pv = target.viewsPlaced[idx];
                store.movePlacedView(sheet.id, pv.id, {
                    x: (pts[0].x + pts[1].x) / 200,
                    y: (pts[0].y + pts[1].y) / 200,
                });
                notify(i18n.t('tools.notify.vpclipRecentered', { index: idx }), 'info');
                return `VPCLIP: viewport ${idx} clipped.`;
            },
        });
        return 'VPCLIP: choose viewport index';
    },

    'file.vpLayer': () => {
        runInteraction({
            steps: [PTxt('VPLAYER: layer name to freeze in the active sheet view')],
            complete: (_p, values) => {
                const name = String(values[0] ?? '').trim();
                const store = useBIMStore.getState();
                const view = store.project.views.get(store.project.activeViewId);
                if (!view || !name) return 'VPLAYER: active view + layer name required.';
                const vis = { ...(view.visibility ?? {}) };
                vis[name] = false;
                store.updateView(store.project.activeViewId, { visibility: vis });
                return `VPLAYER: "${name}" frozen in view "${view.name}".`;
            },
        });
        return 'VPLAYER: layer name';
    },
    'file.vpSync': () => {
        notify(i18n.t('tools.notify.vpsyncPlacedViewportsShareTheSheet'), 'info');
        return 'VPSYNC: sheet viewports synchronized.';
    },
    'space.chspace': () => {
        const store = useBIMStore.getState();
        if (store.project.activeSheetId) {
            store.setActiveSheet(null);
            return 'CHSPACE: back to model space.';
        }
        const sheet = sheetIdOrNew('CHSPACE');
        if (!sheet) return 'CHSPACE: no sheet available.';
        store.setActiveSheet(sheet.id);
        return 'CHSPACE: switched to paper (sheet) space.';
    },
    'file.sharedViews': () => {
        const svg = ledgerSVG();
        downloadTextFile('shared-view.svg', svg);
        notify(i18n.t('tools.notify.sharedviewsCloudPublishingIsNotAvailable'), 'info');
        return 'SHAREDVIEWS: SVG snapshot exported for sharing.';
    },
    // file.plot / file.publish live in plotCommands (Faz 2): the hidden
    // iframe + window.print path and the per-sheet SVG publish were removed
    // in favor of the real vector PDF pipeline (02-plot-pdf-ctb.md, 2.9).
    'file.export': (args) => {
        const fmt = (args?.[0] ?? 'svg').toLowerCase();
        if (fmt === 'dxf') {
            // Native DXF export: the dialog carries the version target
            // (DXF 2018 … R14) and the save flow (§3.2 decision 13).
            openDxfExportDialog({ mode: 'drawing' });
            return 'EXPORT: DXF export dialog opened — pick a version target.';
        }
        if (fmt === 'obj') {
            const objects = Object.values(useDocumentStore.getState().sceneObjects).filter((o) => o.source === 'cad' && o.visible);
            const lines: string[] = ['# Yap OBJ export'];
            let offset = 1;
            objects.forEach((o) => {
                const d = o.data as Record<string, any>;
                const pts: Pt3[] = Array.isArray(d.vertices) ? d.vertices
                    : (d.start && d.end ? [d.start, d.end] : entityPoly(o.id));
                if (!pts || pts.length < 2) return;
                pts.forEach((p) => lines.push(`v ${p[0]} ${-(p[1] ?? 0)} ${p[2] ?? 0}`));
                for (let i = 0; i < pts.length; i++) lines.push(`l ${offset + i} ${offset + ((i + 1) % pts.length)}`);
                offset += pts.length;
            });
            downloadTextFile('export.obj', lines.join('\n'), 'model/object');
            return 'EXPORT: OBJ written (lines/curves).';
        }
        downloadTextFile('export.svg', ledgerSVG());
        return 'EXPORT: SVG written.';
    },
    'file.exportSelected': () => {
        const ids = useAppStore.getState().selectedIds;
        if (ids.length === 0) return 'EXPORTSELECTED: select objects first.';
        const doc = useDocumentStore.getState();
        const objs = ids.map((id) => doc.sceneObjects[id]).filter(Boolean);
        downloadTextFile('selection.json', JSON.stringify(objs, null, 2));
        return `EXPORTSELECTED: ${objs.length} object(s) exported as JSON.`;
    },
};

function entityPoly(id: string): Pt3[] {
    const e = useAppStore.getState().entities[id];
    return e ? (entityToPolyline(e) ?? []) : [];
}
